/**
 * Survey Form Component for Re:Nav-96
 *
 * A reusable form component that renders survey questions based on
 * a dynamic form schema. Supports multiple question types and
 * validation.
 *
 * Features:
 * - Dynamic question rendering based on type
 * - Required field validation
 * - Multiple question types (text, textarea, select, radio, checkbox, etc.)
 * - Progress indicator for multi-question surveys
 * - Submit button with loading state
 *
 * @example
 * <SurveyForm
 *   survey={survey}
 *   onSubmit={handleSubmit}
 *   isSubmitting={isSubmitting}
 * />
 */

import { useState, useCallback, useMemo } from 'react';
import type { SurveyForm as SurveyFormType, SurveyQuestion } from '@/types';

/**
 * Props for the SurveyForm component
 */
interface SurveyFormProps {
  survey: SurveyFormType;
  onSubmit: (responses: Record<string, unknown>) => Promise<void>;
  isSubmitting: boolean;
}

/**
 * Props for individual question components
 */
interface QuestionProps {
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
}

/**
 * Text input question component
 */
function TextQuestion({ question, value, onChange, error }: QuestionProps) {
  return (
    <div className="space-y-2">
      <input
        type="text"
        id={question.id}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.description || 'Enter your answer...'}
        className={`input w-full ${error ? 'border-red-500/50 focus:border-red-500' : ''}`}
      />
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Textarea question component
 */
function TextareaQuestion({ question, value, onChange, error }: QuestionProps) {
  return (
    <div className="space-y-2">
      <textarea
        id={question.id}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.description || 'Enter your answer...'}
        className={`input w-full min-h-[120px] resize-y ${error ? 'border-red-500/50 focus:border-red-500' : ''}`}
      />
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Select dropdown question component
 */
function SelectQuestion({ question, value, onChange, error }: QuestionProps) {
  return (
    <div className="space-y-2">
      <select
        id={question.id}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`input w-full ${error ? 'border-red-500/50 focus:border-red-500' : ''}`}
      >
        <option value="">Select an option...</option>
        {question.options?.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Radio button question component
 */
function RadioQuestion({ question, value, onChange, error }: QuestionProps) {
  return (
    <div className="space-y-3">
      {question.options?.map((option) => (
        <label
          key={option}
          className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors border border-transparent hover:border-white/10"
        >
          <input
            type="radio"
            name={question.id}
            value={option}
            checked={value === option}
            onChange={(e) => onChange(e.target.value)}
            className="w-4 h-4 text-primary bg-white/10 border-white/20 focus:ring-primary"
          />
          <span className="text-white/80">{option}</span>
        </label>
      ))}
      {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
    </div>
  );
}

/**
 * Checkbox question component (multiple selections)
 */
function CheckboxQuestion({ question, value, onChange, error }: QuestionProps) {
  const selectedValues = (value as string[]) || [];

  const handleToggle = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((v) => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  return (
    <div className="space-y-3">
      {question.options?.map((option) => (
        <label
          key={option}
          className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors border border-transparent hover:border-white/10"
        >
          <input
            type="checkbox"
            value={option}
            checked={selectedValues.includes(option)}
            onChange={() => handleToggle(option)}
            className="w-4 h-4 text-primary bg-white/10 border-white/20 rounded focus:ring-primary"
          />
          <span className="text-white/80">{option}</span>
        </label>
      ))}
      {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
    </div>
  );
}

/**
 * Multi-select question component
 */
function MultiSelectQuestion({ question, value, onChange, error }: QuestionProps) {
  const selectedValues = (value as string[]) || [];

  const handleToggle = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((v) => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-white/50 mb-2">Select all that apply</p>
      {question.options?.map((option) => (
        <label
          key={option}
          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
            selectedValues.includes(option)
              ? 'bg-primary/20 border-primary/30'
              : 'bg-white/5 hover:bg-white/10 border-transparent hover:border-white/10'
          }`}
        >
          <input
            type="checkbox"
            value={option}
            checked={selectedValues.includes(option)}
            onChange={() => handleToggle(option)}
            className="w-4 h-4 text-primary bg-white/10 border-white/20 rounded focus:ring-primary"
          />
          <span className="text-white/80">{option}</span>
        </label>
      ))}
      {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
    </div>
  );
}

/**
 * Number input question component
 */
function NumberQuestion({ question, value, onChange, error }: QuestionProps) {
  return (
    <div className="space-y-2">
      <input
        type="number"
        id={question.id}
        value={value !== undefined && value !== null ? String(value) : ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder={question.description || 'Enter a number...'}
        min={question.validation?.min}
        max={question.validation?.max}
        className={`input w-full max-w-[200px] ${error ? 'border-red-500/50 focus:border-red-500' : ''}`}
      />
      {question.validation && (
        <p className="text-xs text-white/40">
          {question.validation.min !== undefined && `Min: ${question.validation.min}`}
          {question.validation.min !== undefined && question.validation.max !== undefined && ' | '}
          {question.validation.max !== undefined && `Max: ${question.validation.max}`}
        </p>
      )}
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Date input question component
 */
function DateQuestion({ question, value, onChange, error }: QuestionProps) {
  return (
    <div className="space-y-2">
      <input
        type="date"
        id={question.id}
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`input w-full max-w-[200px] ${error ? 'border-red-500/50 focus:border-red-500' : ''}`}
      />
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Render appropriate input component based on question type
 */
function QuestionInput(props: QuestionProps) {
  const { question } = props;

  switch (question.type) {
    case 'text':
      return <TextQuestion {...props} />;
    case 'textarea':
      return <TextareaQuestion {...props} />;
    case 'select':
      return <SelectQuestion {...props} />;
    case 'radio':
      return <RadioQuestion {...props} />;
    case 'checkbox':
      return <CheckboxQuestion {...props} />;
    case 'multiselect':
      return <MultiSelectQuestion {...props} />;
    case 'number':
      return <NumberQuestion {...props} />;
    case 'date':
      return <DateQuestion {...props} />;
    default:
      return <TextQuestion {...props} />;
  }
}

/**
 * Progress bar component
 */
function ProgressBar({ current, total }: { current: number; total: number }) {
  const percentage = Math.round((current / total) * 100);

  return (
    <div className="mb-6">
      <div className="flex justify-between text-sm text-white/60 mb-2">
        <span>Progress</span>
        <span>
          {current} of {total} answered ({percentage}%)
        </span>
      </div>
      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * SurveyForm Component
 *
 * Renders a dynamic survey form based on the provided schema.
 * Handles validation and submission.
 *
 * @param props - Component props including survey data and handlers
 * @returns The survey form component
 */
export function SurveyForm({ survey, onSubmit, isSubmitting }: SurveyFormProps) {
  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const questions = survey.formSchema.questions;
  const settings = survey.formSchema.settings;
  const showProgress = settings?.showProgressBar ?? questions.length > 5;

  /**
   * Calculate number of answered questions
   */
  const answeredCount = useMemo(() => {
    return questions.filter((q) => {
      const value = responses[q.id];
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }).length;
  }, [questions, responses]);

  /**
   * Handle response change
   */
  const handleChange = useCallback((questionId: string, value: unknown) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
    setTouched((prev) => new Set(prev).add(questionId));

    // Clear error when field is changed
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[questionId];
      return newErrors;
    });
  }, []);

  /**
   * Validate all responses
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    for (const question of questions) {
      const value = responses[question.id];
      const isEmpty =
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

      if (question.required && isEmpty) {
        newErrors[question.id] = 'This field is required';
      }

      // Number validation
      if (question.type === 'number' && !isEmpty) {
        const numValue = Number(value);
        if (question.validation?.min !== undefined && numValue < question.validation.min) {
          newErrors[question.id] = `Value must be at least ${question.validation.min}`;
        }
        if (question.validation?.max !== undefined && numValue > question.validation.max) {
          newErrors[question.id] = `Value must be at most ${question.validation.max}`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [questions, responses]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Mark all fields as touched
      setTouched(new Set(questions.map((q) => q.id)));

      if (!validate()) {
        // Scroll to first error
        const firstError = document.querySelector('[data-error="true"]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      await onSubmit(responses);
    },
    [questions, responses, validate, onSubmit]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Progress bar */}
      {showProgress && <ProgressBar current={answeredCount} total={questions.length} />}

      {/* Questions */}
      <div className="space-y-8">
        {questions.map((question, index) => (
          <div
            key={question.id}
            data-error={Boolean(errors[question.id])}
            className={`card transition-colors ${
              errors[question.id] ? 'border-red-500/30' : ''
            }`}
          >
            {/* Question header */}
            <div className="flex items-start gap-3 mb-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 text-primary font-semibold flex items-center justify-center text-sm">
                {index + 1}
              </span>
              <div className="flex-1">
                <label
                  htmlFor={question.id}
                  className="block text-lg font-medium text-white"
                >
                  {question.label}
                  {question.required && (
                    <span className="text-primary ml-1">*</span>
                  )}
                </label>
                {question.description && question.type !== 'text' && question.type !== 'textarea' && (
                  <p className="text-white/50 text-sm mt-1">{question.description}</p>
                )}
              </div>
            </div>

            {/* Question input */}
            <QuestionInput
              question={question}
              value={responses[question.id]}
              onChange={(value) => handleChange(question.id, value)}
              error={touched.has(question.id) ? errors[question.id] : undefined}
            />
          </div>
        ))}
      </div>

      {/* Submit button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary px-8 py-3 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin h-5 w-5"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Submitting...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {settings?.submitButtonText || 'Submit'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default SurveyForm;
